import { useCallback } from 'react';
import { useNotifications } from '@/providers/notificationHooks';
import type { SuiteExecutionDto, TestExecutionDto } from '@flowqa/shared';

export type DetailItem = { label: string; value: string };

export const buildPanelText = (items: DetailItem[]): string =>
  items
    .map((item) => `${item.label}:\n${item.value}`)
    .join('\n\n')
    .trim();

export const buildSuiteDetails = (execution: SuiteExecutionDto): DetailItem[] => {
  const items: DetailItem[] = [];
  if (execution.systemOut) items.push({ label: 'System Out', value: execution.systemOut });
  if (execution.systemErr) items.push({ label: 'System Err', value: execution.systemErr });
  return items;
};

export const buildTestDetails = (execution: TestExecutionDto): DetailItem[] => {
  const items: DetailItem[] = [];
  if (execution.failureMessage) {
    items.push({ label: 'Failure Message', value: execution.failureMessage });
  }
  if (execution.failureType) {
    items.push({ label: 'Failure Type', value: execution.failureType });
  }
  if (execution.failureStack) {
    items.push({ label: 'Failure Stack', value: execution.failureStack });
  }
  if (execution.systemOut) {
    items.push({ label: 'System Out', value: execution.systemOut });
  }
  if (execution.systemErr) {
    items.push({ label: 'System Err', value: execution.systemErr });
  }
  return items;
};

export const useCopyPanel = (): ((title: string, items: DetailItem[]) => void) => {
  const { notify } = useNotifications();
  return useCallback(
    (title: string, items: DetailItem[]): void => {
      const payload = buildPanelText(items);
      if (!payload) return;
      void navigator.clipboard.writeText(payload);
      notify({ message: `${title} copied to clipboard`, tone: 'green' });
    },
    [notify],
  );
};
